import type {
  DataDumpAggregateType,
  PnlChartSectionType,
} from "../../types/types";
import { useGetPnlChartForCustomer } from "../../providers/GlobalStateProvider";
import { useSelector } from "store/useSelector";
import {
  filterPnlChartsForSerialProcess,
  rollupSingleProcessBreakdownCharts,
} from "components/Dashboard/PnlSummaryChart/pnlSummaryChartUtils";
import { filterPnlChartsForTrendsPage } from "components/Dashboard/PnlSummaryChart/pnlSummaryChartUtilsForTrendsPage";
import { useCustomerKey } from "./useCustomerKey";
import { calcPnlChartCategories } from "components/Dashboard/PnlSummaryChart/calcPnlChartCategories";
import {
  useGetPageFromPath,
  useIsSingleProcessPage,
  useIsSerialProcess,
  useFullRangeDataDump,
  useTrendsPageFilterState,
  useFocusRange,
  useSelectedProcesses,
  useVisibleProcesses,
  useVisibleGroups,
} from "./genericHooks";
import { useShouldUseDataDumpsForPnlChart } from "./whichServerHooks";
import { OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART } from "constants/customers";
import { useCalcPnlChartCategoriesWithProducingLosses } from "components/Dashboard/PnlSummaryChart/calcPnlChartCategoriesWithProducingLosses";
import { useFilteredShifts } from "./trendChartHooks";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { mapEntries } from "@raven/shared/utils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

/** get the pnl breakdown chart for a process */
export function usePnlSummaryChartSectionsForProcess(
  processId: string
): PnlChartSectionType[] {
  const dataDumpForFocusRangeForProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess[processId]
  );
  const singleProcessRollupMetric = useSelector(
    state => state.legacy.rollupsMetricByProcess[processId]
  );
  const getPnlChartForCustomer = useGetPnlChartForCustomer();
  return getPnlChartForCustomer(
    dataDumpForFocusRangeForProcess || null,
    singleProcessRollupMetric
  );
}

/** get the pnl breakdown charts for many processes */
function usePnlSummaryChartSectionsForManyProcesses(processIds: string[]): {
  [processId: string]: PnlChartSectionType[];
} {
  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );
  const rollupsMetricByProcess = useSelector(
    state => state.legacy.rollupsMetricByProcess
  );
  const getPnlChartForCustomer = useGetPnlChartForCustomer();

  return processIds.reduce(
    (acc, processId) => ({
      ...acc,
      [processId]: getPnlChartForCustomer(
        dataDumpsFullRangeByProcess[processId] || null,
        rollupsMetricByProcess[processId]
      ),
    }),
    {}
  );
}

/** pnl chart data for the current situation:
 *
 * 1. default (non-serial, no PnlLosses): all-processes-rollup from Askr
 *
 * 2. trends page: filter based on trends page filter state, & only use Askr data for now
 *
 * 3. serial process: filter based on selected processes
 *
 * 4. non-serial process with PnlLosses: data sourced from Midgard (e.g. Quality Loss, Speed Loss, Counts)
 *
 * @param shouldCalculate don't bother doing any work if we're not even going to use it
 * (hook has to be called due to the rules of hooks, so we manually skip the calculations)
 */
function usePnlChartFromAskrOrMidgard({
  shouldCalculate,
  isSubordinateProcessesOnly,
}: {
  shouldCalculate: boolean;
  isSubordinateProcessesOnly?: boolean;
}) {
  const ckey = useCustomerKey();
  const allProcessesRollupMetric = useSelector(
    state => state.legacy.allProcessesRollupMetric
  );
  const hiddenPnlChartCategories = useSelector(
    state => state.legacy.hiddenPnlChartCategories
  );
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );
  const { isTrendsPage } = useGetPageFromPath();
  const shouldUsePnlLossesData = useShouldUseDataDumpsForPnlChart();

  const isSingleProcessPage = useIsSingleProcessPage();
  const isSerialProcess = useIsSerialProcess();
  const dataDumpFullRange = useFullRangeDataDump();
  const { trendsPageFilterState, isTrendsPageFilteredByShift } =
    useTrendsPageFilterState();
  const focusRange = useFocusRange();
  const selectedProcessIdsRef = useSelectedProcesses();
  const visibleProcesses = useVisibleProcesses();
  const visibleGroups = useVisibleGroups();
  const SPChartsArr = useSingleProcessPnlChartsArr();
  const dataDumpFullRangeSubordinateProcesses = useSelector(
    state => state.legacy.dataDumpFullRangeSubordinateProcesses
  );
  const calcPnlChartCategoriesWithProducingLosses =
    useCalcPnlChartCategoriesWithProducingLosses();
  const filteredShifts = useFilteredShifts();
  // Should only be one group shown on timeline page
  const groupForTimelinePage = visibleGroups?.[0]?.summaryGroup;
  const shouldUseOverridenProcesses =
    OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART?.[ckey]?.[
      groupForTimelinePage
    ] !== undefined;

  const [
    {
      general: { enableSecondaryProcessesPnlChart },
    },
  ] = useFlightConfig();

  // don't bother doing any work if we're not even going to use it
  if (!shouldCalculate) {
    return [];
  }

  // 1. default (non-serial, no PnlLosses): all-processes-rollup from Askr
  let pnlBreakdownChartCategorySectionsDisplay = calcPnlChartCategories(
    allProcessesRollupMetric,
    hiddenPnlChartCategories,
    dataDumpFullRange
  );

  if (isTrendsPage) {
    // 2. trends page: filter based on trends page filter state, & only use Askr data for now
    // (trends page can't use datadump until midgard grandular caching is ready)
    const filteredShift = filteredShifts.find(
      shift =>
        // find the matching shift in the focus range
        focusRange &&
        shift.shift.name === trendsPageFilterState.shiftName &&
        shift.range.ge >= focusRange?.ge &&
        shift.range.ge < focusRange?.lt
    );

    const isTrendsPage24HrShiftMode =
      isTrendsPage &&
      isTrendsPageFilteredByShift &&
      filteredShift &&
      focusRange &&
      focusRange.lt - focusRange.ge <= ONE_DAY_OF_MS;

    pnlBreakdownChartCategorySectionsDisplay = filterPnlChartsForTrendsPage({
      allRollupsMetricTrendChartBarsArrays: isTrendsPage24HrShiftMode
        ? mapEntries(
            allRollupsMetricTrendChartBarsArrays,
            ([processId, bars]) => [
              processId,
              bars.filter(
                bar =>
                  bar.range.ge >= filteredShift?.range.ge &&
                  bar.range.lt <= filteredShift?.range.lt
              ),
            ]
          )
        : allRollupsMetricTrendChartBarsArrays,
      visibleGroups,
      trendsPageFilterState,
      SPChartsArr,
      visibleProcesses,
      hiddenPnlChartCategories: [],
      focusRange: isTrendsPage24HrShiftMode ? filteredShift?.range : focusRange,
    });
  } else if (!isTrendsPage) {
    // 3. serial process: filter based on selected processes
    if (
      (isSerialProcess || enableSecondaryProcessesPnlChart) &&
      !isSingleProcessPage
    ) {
      // for serial process, get either "serial" processes or "subordinate" processes (i.e. serial "constraint processes" vs "other processes")
      if (isSubordinateProcessesOnly) {
        if (!dataDumpFullRangeSubordinateProcesses) {
          return;
        }

        const pnlChartFromSubordinateProcessesDDA =
          calcPnlChartCategoriesWithProducingLosses(
            dataDumpFullRangeSubordinateProcesses
          );

        pnlBreakdownChartCategorySectionsDisplay =
          rollupSingleProcessBreakdownCharts(
            [pnlChartFromSubordinateProcessesDDA],
            hiddenPnlChartCategories,
            shouldUsePnlLossesData
          );
      } else {
        const processIdsToKeep = selectedProcessIdsRef.current;
        pnlBreakdownChartCategorySectionsDisplay =
          shouldUsePnlLossesData && dataDumpFullRange
            ? calcPnlChartCategoriesWithProducingLosses(dataDumpFullRange)
            : filterPnlChartsForSerialProcess({
                // quality loss p&l chart comes from the data dump
                SPChartsArr,
                processIdsToKeep,
                hiddenPnlChartCategories,
                isCustomerWhoUsesQualityLossPnlChart: shouldUsePnlLossesData,
              });
      }
    } else if (shouldUseOverridenProcesses && isSubordinateProcessesOnly) {
      // 4. non-serial process, but still wants to show "other" pnl chart with OVERRIDE_MACHEMETRY_IDS_FOR_OTHER_PROCESSES_PNL_CHART
      if (!dataDumpFullRangeSubordinateProcesses) {
        return;
      }

      const pnlChartFromSubordinateProcessesDDA =
        calcPnlChartCategoriesWithProducingLosses(
          dataDumpFullRangeSubordinateProcesses
        );

      pnlBreakdownChartCategorySectionsDisplay =
        rollupSingleProcessBreakdownCharts(
          [pnlChartFromSubordinateProcessesDDA],
          hiddenPnlChartCategories,
          shouldUsePnlLossesData
        );
    } else if (shouldUsePnlLossesData && dataDumpFullRange) {
      // 5. non-serial process with PnlLosses: data sourced from Midgard (e.g. Quality Loss, Speed Loss, Counts)
      // for non-serial process, use all processes
      pnlBreakdownChartCategorySectionsDisplay =
        calcPnlChartCategoriesWithProducingLosses(dataDumpFullRange);
    }
  }

  return pnlBreakdownChartCategorySectionsDisplay;
}

/** get an array of pnl charts per-process, sourced either from dataDump or askr rollups */
function useSingleProcessPnlChartsArr(): [string, PnlChartSectionType[]][] {
  const shouldUseDataDumps = useShouldUseDataDumpsForPnlChart();

  const dataDumpsFullRangeByProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess
  );

  const visibleProcesses = useVisibleProcesses();

  // on some pages / customers, we need to roll up charts for single processes (e.g. filtered/serial processes, or hublet page)
  const singleProcessBreakdownChartsDisplay =
    usePnlSummaryChartSectionsForManyProcesses(visibleProcesses.map(p => p.id));

  const SPChartsArrFromAskr: [string, PnlChartSectionType[]][] = Object.entries(
    singleProcessBreakdownChartsDisplay
  );
  const calcPnlChartCategoriesWithProducingLosses =
    useCalcPnlChartCategoriesWithProducingLosses();

  // data dump users get singleProcessBreakdownCharts from midgard dataDumps
  const SPChartsArrFromDataDumps: [string, PnlChartSectionType[]][] =
    Object.entries(dataDumpsFullRangeByProcess).map(([processId, dataDump]) => [
      processId,
      calcPnlChartCategoriesWithProducingLosses(
        dataDump as DataDumpAggregateType
      ),
    ]);

  return shouldUseDataDumps // trends page can't use datadumps yet (until midgard granular caching)
    ? SPChartsArrFromDataDumps
    : SPChartsArrFromAskr;
}

/** grab the all-processes-rollup chart, or roll up individual charts,
 * or just grab a single process chart depending what page/customer you’re on
 */
// tslint:disable-next-line cognitive-complexity
export function usePnlSummaryChartSections({
  isPopup,
  isSubordinateProcessesOnly,
}: {
  isPopup: boolean;
  isSubordinateProcessesOnly?: boolean;
}): PnlChartSectionType[] {
  const isSingleProcessPage = useIsSingleProcessPage();
  const visibleProcesses = useVisibleProcesses();

  const { isTrendsPage } = useGetPageFromPath();
  const SPChartsArr = useSingleProcessPnlChartsArr();

  // we'll show PnL data from either Askr or Midgard here:
  // - single process charts come from Askr aggregates
  // - quality loss charts come from Midgard dataDumpAggregates
  const showSingleProcessChart =
    isSingleProcessPage || (isPopup && !isTrendsPage); // popup chart passes in its own data

  const pnlBreakdownChartCategorySectionsDisplay = usePnlChartFromAskrOrMidgard(
    { shouldCalculate: !showSingleProcessChart, isSubordinateProcessesOnly }
  );
  if (showSingleProcessChart) {
    // check if we're in a single-process page -> if so, use the singleProcessBreakdownAskrCharts
    const singleProcessChart = SPChartsArr.find(
      ([processId, pnlBreakdownCharts]) => processId === visibleProcesses[0]?.id
    )?.[1];

    return singleProcessChart || [];
  } else {
    return pnlBreakdownChartCategorySectionsDisplay || [];
  }
}
